import dayjs from 'dayjs';
import { eachDayOfInterval } from 'date-fns';
import { SettingLocalStorageRepository } from '../../user-settings/repository/setting-local-storage-repository';
import { DateTimeFormats } from '../models/susoft-date-time-format';

/**
 * Maps dayjs/JS day-of-week index (0 = Sunday … 6 = Saturday) to the uppercase
 * day-of-week name used in operating-hours / event-series models.
 */
export const DAY_OF_WEEK_NAMES: Array<
  'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY'
> = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

// For material Date picker
let dateFormat = SettingLocalStorageRepository.getDateFormat();
dateFormat = dateFormat?.replace(/dd/g, 'DD');

// For material Date picker
export const MY_FORMATS = {
    parse: {
        dateInput: 'LL'
    },
    display: {
        dateInput: dateFormat,
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'YYYY'
    }
};

export const calculateDates = (from, to) => {
    const interval = {
        start: from,
        end: to,
    };

    return eachDayOfInterval(interval);
};

export const getDiffInDays = (date1: dayjs.Dayjs | Date, date2: dayjs.Dayjs | Date): number => {
    return dayjs(date1).startOf('day').diff(dayjs(date2).startOf('day'), 'day');
}

export class MomentRange {
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;

    public static isOverlap(interval1: MomentRange, interval2: MomentRange): boolean {
        if (interval1.startDate.startOf('day').diff(interval2.endDate.startOf('day'), 'day') <= 0
            && interval2.startDate.startOf('day').diff(interval1.endDate.startOf('day'), 'day') <= 0)
            return true;
        return false;
    }

    public static getDatesInRange(range: MomentRange) {
        const daysList = calculateDates(range.startDate.toDate(), range.endDate.toDate());
        const chartDates = daysList.sort((a, b) => { return dayjs(a).diff(dayjs(b), 'd') });
        return chartDates;
    }

}

export const addSingleYear = (date: string) => {
    return getDateInYYYYMMDD(dayjs(date).add(1, 'year').toDate());
}

export const subtractSingleYear = (date: string) => {
    return getDateInYYYYMMDD(dayjs(date).subtract(1, 'year').toDate());
}

export const addWeek = (date: string) => {
    return getDateInYYYYMMDD(dayjs(date).add(7, 'day').toDate());
}

export const subtractWeek = (date: string) => {
    return getDateInYYYYMMDD(dayjs(date).subtract(7, 'day').toDate());
}

export const addSingleMonth = (date: string) => {
    return dayjs(date).add(30, 'day').toDate().getFullYear();
}

export const getFullYearFromNextYear = (date: string) => {
    return dayjs(date).add(1, 'year').toDate().getFullYear();
}

export const getFullYear = (date: string) => {
    return dayjs(date).toDate().getFullYear();
}

export const getNextMonth = (currentMonth: number) => {
    return currentMonth.toString().padStart(2, '0');
}

export const getDay = (date: number) => {
    return  dayjs().date(date).toDate().getDate().toString().padStart(2, '0');
}

/**
 * Convert a date to the canonical server date format.
 */
export const toServerDate = (
    date: Date | dayjs.Dayjs | string,
    format: string = DateTimeFormats.YEAR_MONTH_DAY_DASH
): string => {
    return dayjs(date).format(format);
};

/**
 * Convert a date/time to the canonical server date-time format.
 */
export const toServerDateTime = (
    date: Date | dayjs.Dayjs | string,
    format: string = DateTimeFormats.SERVER_DATE_TIME_FORMAT
): string => {
    return dayjs(date).format(format);
};

/**
 * Parse a server date string.
 */
export const parseServerDate = (
    date: string,
    format: string = DateTimeFormats.YEAR_MONTH_DAY_DASH
): dayjs.Dayjs => {
    return dayjs(date, format);
};

/**
 * Parse a server date-time string.
 */
export const parseServerDateTime = (
    dateTime: string,
    format: string = DateTimeFormats.SERVER_DATE_TIME_FORMAT
): dayjs.Dayjs => {
    return dayjs(dateTime, format);
};


// Function to get the date in the YYYYMMDD format
export const getDateInYYYYMMDD = (date: Date): string => {

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}${month}${day}`;
}